import { useState } from 'react';
import { useModalFocusTrap } from '../useModalFocusTrap';
import {
  DecisionIcon,
  actionTone,
  reviewPrimaryButton,
  reviewQuietButton,
} from './ReviewUi';
import { SubmissionCeremonyScene, TourScene } from './OnboardingTourScenes';

const TOTAL_STEPS = 6;

interface OnboardingTourProps {
  dismissible: boolean;
  allowSkip: boolean;
  onComplete: () => void;
  onSkip: () => void;
  onDismiss?: () => void;
}

function DecisionDefinition({
  action,
  children,
}: {
  action: 'use' | 'maybe' | 'remove';
  children: string;
}) {
  const label = action === 'use' ? 'Use' : action === 'maybe' ? 'Maybe' : 'Remove';
  return (
    <li className="flex items-center gap-3 text-[13px] text-muted">
      <span className={`inline-flex min-h-[26px] min-w-[5.4rem] items-center justify-center gap-1 rounded-full border px-3 text-xs font-semibold ${actionTone[action]}`}>
        <DecisionIcon action={action} className="h-3 w-3" />
        {label}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Recap() {
  const items = ['Watch & read', 'Mark & comment', 'Check selects', 'Submit'];
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" aria-label="Review workflow recap">
      {items.map((item, index) => (
        <span key={item} className="inline-flex items-center gap-1.5">
          {index > 0 ? <span className="text-line" aria-hidden="true">→</span> : null}
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.06em] ${index === items.length - 1 ? 'border-accent/35 bg-accent-tint text-accent' : 'border-line bg-card text-muted'}`}>
            <span className={`grid h-4 w-4 place-items-center rounded-full text-[9px] font-semibold ${index === items.length - 1 ? 'bg-accent text-white' : 'bg-elevated-2 text-muted'}`}>{index + 1}</span>
            {item}
          </span>
        </span>
      ))}
    </div>
  );
}

export function OnboardingTourStepContent({ step }: { step: number }) {
  if (step === 1) {
    return (
      <>
        <h2 id="onboarding-tour-title" className="title-lg">Welcome to your review.</h2>
        <p className="mt-2 text-[14px] leading-[1.55] text-muted">A quick tour of how to watch your interviews, mark the moments you want, and send it all back to Henry. It works the same way for every review you receive.</p>
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        <h2 id="onboarding-tour-title" className="title-lg">Watch and read, side by side.</h2>
        <p className="mt-2 text-[14px] leading-[1.55] text-muted">Each interview has its written transcript on the left and the video beside it. Watch, read, or do both. Click any timestamp in the transcript and the video jumps straight to that line.</p>
        <p className="mt-2 text-[12.5px] leading-[1.55] text-faint">Not every review has a video. Some are voice recordings, and some videos arrive later. You can always read and mark the transcript.</p>
        <TourScene step={step} />
      </>
    );
  }

  if (step === 3) {
    return (
      <>
        <h2 id="onboarding-tour-title" className="title-lg">Mark what stands out.</h2>
        <p className="mt-2 text-[14px] leading-[1.55] text-muted">Highlight any words in the transcript, or use Mark this paragraph to mark a whole one. A small window appears where you choose what happens to that moment:</p>
        <ul className="mt-3 grid gap-2">
          <DecisionDefinition action="use">You want this in your video.</DecisionDefinition>
          <DecisionDefinition action="maybe">Could work in your video if it is needed.</DecisionDefinition>
          <DecisionDefinition action="remove">You do not want this in your video.</DecisionDefinition>
        </ul>
        <TourScene step={step} />
      </>
    );
  }

  if (step === 4) {
    return (
      <>
        <h2 id="onboarding-tour-title" className="title-lg">Say it with a comment.</h2>
        <p className="mt-2 text-[14px] leading-[1.55] text-muted">Some feedback is not a yes or a no. Highlight a line and write a comment in the same little window. It appears in the Comments panel under the video, pinned to that exact moment, so Henry sees exactly what you mean.</p>
        <p className="mt-2 text-[12.5px] leading-[1.55] text-faint">You can also add a comment while choosing Use, Maybe or Remove, or leave a general note straight in the Comments panel.</p>
        <TourScene step={step} />
      </>
    );
  }

  if (step === 5) {
    return (
      <>
        <h2 id="onboarding-tour-title" className="title-lg">Your selects, all in one place.</h2>
        <p className="mt-2 text-[14px] leading-[1.55] text-muted">Every moment you mark Use or Maybe is called a select. When you have been through each interview, open Selects at the top of the page: everything you have chosen, from every interview, gathers on one page. Review the full story there and change any decision before you send it.</p>
        <TourScene step={step} />
      </>
    );
  }

  return (
    <>
      <h2 id="onboarding-tour-title" className="title-lg">Send it to Henry.</h2>
      <p className="mt-2 text-[14px] leading-[1.55] text-muted">When you are happy with your selects, submit your review. Nothing is final until the project closes, so you can keep making changes until then.</p>
      <Recap />
      <SubmissionCeremonyScene />
    </>
  );
}

export default function OnboardingTour({
  dismissible,
  allowSkip,
  onComplete,
  onSkip,
  onDismiss,
}: OnboardingTourProps) {
  const [step, setStep] = useState(1);
  const dismiss = () => onDismiss?.();
  const closeTour = dismissible ? dismiss : undefined;
  const panelRef = useModalFocusTrap<HTMLDivElement>(closeTour);

  return (
    <div
      className="app-scrim fade-in fixed inset-0 z-[70] grid place-items-center p-4"
      role="presentation"
      onMouseDown={closeTour
        ? (event) => {
          if (event.target === event.currentTarget) closeTour();
        }
        : undefined}
    >
      <div
        ref={panelRef}
        className={`scale-in max-h-[90vh] w-full overflow-y-auto rounded-card border border-line bg-card shadow-[var(--shadow-floating)] ${step === 1 ? 'max-w-[480px]' : 'max-w-[640px]'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-tour-title"
        tabIndex={-1}
      >
        <div className="flex items-center justify-between gap-4 px-6 pt-5">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-faint">Step {step} of {TOTAL_STEPS}</span>
          <div className="ml-auto flex items-center gap-[5px]" aria-label={`Step ${step} of ${TOTAL_STEPS}`}>
            {Array.from({ length: TOTAL_STEPS }, (_, index) => {
              const dotStep = index + 1;
              const tone = dotStep < step
                ? 'bg-accent/40'
                : dotStep === step
                  ? 'w-4 bg-accent'
                  : 'bg-line';
              return <span key={dotStep} className={`h-1.5 w-1.5 rounded-full ${tone}`} aria-hidden="true" />;
            })}
          </div>
          {dismissible ? (
            <button
              type="button"
              className="-my-2 -mr-2 min-h-11 min-w-11 rounded-full text-xl font-bold text-faint hover:bg-elevated hover:text-fg focus-visible:outline focus-visible:outline-[2.5px] focus-visible:outline-offset-2 focus-visible:outline-accent"
              onClick={dismiss}
              aria-label="Close tour"
            >
              ×
            </button>
          ) : null}
        </div>

        <div className="px-6 pb-1 pt-4" aria-live="polite">
          <OnboardingTourStepContent step={step} />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line px-6 py-5">
          {allowSkip && !dismissible ? (
            <button
              type="button"
              className="whitespace-nowrap rounded-full font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-faint underline-offset-4 hover:text-muted hover:underline focus-visible:outline focus-visible:outline-[2.5px] focus-visible:outline-offset-3 focus-visible:outline-accent"
              onClick={onSkip}
            >
              Skip tour
            </button>
          ) : null}
          <span className="min-w-0 flex-1" />
          {step > 1 ? (
            <button type="button" className={`${reviewQuietButton} whitespace-nowrap`} onClick={() => setStep((current) => current - 1)}>Back</button>
          ) : null}
          <button
            type="button"
            className={`${reviewPrimaryButton} ml-auto whitespace-nowrap`}
            onClick={() => {
              if (step === TOTAL_STEPS) onComplete();
              else setStep((current) => current + 1);
            }}
          >
            {step === TOTAL_STEPS ? 'Start reviewing' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
